import { MouseEventHandler, useMemo } from "react"

import { useI18next } from "gatsby-plugin-react-i18next"
import styled from "styled-components"

import SeafoamLine from "components/buttons/SeafoamLine"
import colors from "styles/colors"
import easing from "styles/easing"
import media from "styles/media"
import text from "styles/text"
import { checkedLocaleLink } from "utils/routes"
import useMedia from "utils/useMedia"
import FeaturedProduct from "./FeaturedProduct"
import PopoutLinkCard from "./PopoutLinksCard"
import { Section } from "components/Header"

// Type for tab prop
type Tab = {
  title:
    | "Deposits"
    | "Engagement Channels"
    | "Payments & Cards"
    | "Lending"
    | "Risk"
    | "Platform"
    | "Company"
  description: string
  image: string
  links: Section[]
}

// Props for HeaderPopoutBox component
interface HeaderPopoutBoxProps {
  tab: Tab
  open: boolean
  index: number
  translation: Record<string, string> // translation dictionary
  languageOverride: string | undefined
}
export default function HeaderPopoutBox({
  tab,
  open,
  index,
  translation,
  languageOverride,
}: HeaderPopoutBoxProps) {
  const heightDic: any = {
    Deposits: useMedia("462px", "27.831vw", "45.117vw", "27.831vw"),
    Channels: useMedia("462px", "27.831vw", "45.117vw", "27.831vw"),
    "Payments & Cards": useMedia("368px", "22.169vw", "35.938vw", "27.410vw"),
    Lending: useMedia("411px", "24.759vw", "40.137vw", "24.759vw"),
    Risk: useMedia("411px", "24.759vw", "40.137vw", "24.759vw"),
    Platform: useMedia("498px", "30.000vw", "48.633vw", "30.000vw"),
    Company: useMedia("498px", "30.000vw", "48.633vw", "30.000vw"),
  }
  const { language } = useI18next()

  const subData = tab.links.slice(0, -1)
  return open ? (
    <NavFloaterEl
      index={index}
      onClick={e => e.stopPropagation()}
      onKeyUp={e => e.stopPropagation()}
      h={heightDic[tab.title]}
      role="presentation"
    >
      <MainContainer>
        <Top>
          <Logo src={tab.image} alt="logo" />
          <TextContainer>
            <Title>{translation[tab.title]}</Title>
            <Description>{translation[tab.description]}</Description>
          </TextContainer>
        </Top>
        <Line />
        <FeaturedProduct
          links={tab.links[tab.links.length - 1]}
          title={tab.title}
          translation={translation}
          languageOverride={languageOverride}
        />
      </MainContainer>
      <LeftContainer>
        {subData.map((ele: Section) => {
          if (ele.left) {
            return (
              <PopoutLinkCard
                key={ele.title}
                title={ele.title}
                links={ele.links}
                translation={translation}
                languageOverride={languageOverride}
              />
            )
          }
          return <></>
        })}
      </LeftContainer>
      <RightContainer>
        {subData.map((ele: Section) => {
          if (!ele.left) {
            return (
              <PopoutLinkCard
                key={ele.title}
                title={ele.title}
                links={ele.links}
                translation={translation}
                languageOverride={languageOverride}
              />
            )
          }
          return <></>
        })}
      </RightContainer>
    </NavFloaterEl>
  ) : null
}

const NavFloaterEl = styled.div<{
  index: number
  onClick: MouseEventHandler
  h: string
}>`
  z-index: 5;
  position: absolute;
  top: ${({ index }) => (index - 1) * 1.153}vw;
  right: 100%;
  width: 73.072vw;
  background-color: ${colors.blue800};
  transition: 0.3s;
  transition-timing-function: ${easing.main};
  overflow: hidden;
  border-radius: 0.602vw;
  display: flex;
  ${media.fullWidth} {
    width: 1100px;
    top: ${({ index }) => (index - 1) * 45}px;
  }
  ${media.tablet} {
    width: 72.613vw;
    top: ${({ index }) => index * 0}vw;
  }
`
const MainContainer = styled.div`
  ${media.fullWidth} {
    margin-left: 35px;
    margin-top: 40px;
  }
  ${media.tablet} {
    margin-left: 2.418vw;
    margin-top: 3.906vw;
  }
  margin-top: 2.41vw;
  margin-left: 2.108vw;
  width: 30vw;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`
const LeftContainer = styled.div`
  ${media.fullWidth} {
    margin-left: 25px;
    margin-top: 40px;
    margin-bottom: 40px;
  }
  ${media.tablet} {
    margin-left: 2.418vw;
    margin-top: 3.906vw;
    margin-bottom: 3.906vw;
  }
  margin-top: 2.41vw;
  margin-bottom: 2.41vw;
  margin-left: 1.108vw;
  width: 20vw;
`
const RightContainer = styled.div`
  ${media.fullWidth} {
    margin-left: 25px;
    margin-top: 40px;
    margin-bottom: 40px;
  }
  ${media.tablet} {
    margin-left: 2.418vw;
    margin-top: 3.906vw;
    margin-bottom: 3.906vw;
  }
  margin-top: 2.41vw;
  margin-bottom: 2.41vw;
  margin-left: 1.108vw;
  width: 20vw;
`
const Top = styled.div`
  display: flex;
`

const Logo = styled.img`
  width: 7.53vw;
  height: 7.53vw;
  ${media.fullWidth} {
    width: 125px;
    height: 125px;
  }
  ${media.tablet} {
    width: 12.207vw;
    height: 12.207vw;
  }
`
const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const Title = styled.h5`
  color: #ffffff;
  font-size: 3vw;
  margin: 0;
  text-transform: capitalize;
  ${text.h5}
`

const Description = styled.p`
  color: #aab1f2;
  width: 17.819vw;
  ${text.bodyS}
  margin-top: 0.602vw;
  margin-bottom: 1.205vw;
  ${media.fullWidth} {
    width: 285px;
    margin-top: 10px;
    margin-bottom: 20px;
  }
  ${media.tablet} {
    width: 18.129vw;
    margin-top: 0.977vw;
    margin-bottom: 1.953vw;
  }
`

const Line = styled.div`
  ${media.fullWidth} {
    margin: 0 50px;
    height: 1px;
  }
  ${media.desktop} {
    margin: 0 3.012vw;
  }
  ${media.tablet} {
    height: 0.098vw;
    margin: 0 4.883vw;
  }
  height: 0.06vw;
  background-color: ${colors.blue700};
`

const LinksEl = styled.div`
  ${media.fullWidth} {
    margin: 50px;
    margin-top: 25px;
  }
  ${media.tablet} {
    margin: 4.883vw;
    margin-top: 2.441vw;
  }
  display: flex;
  flex-direction: column;
  margin: 3.012vw;
  margin-top: 1.506vw;
`
